import { ElementRect, getElementAABB } from "./geometry"

export type Alignment = "left" | "right" | "top" | "bottom" | "centerX" | "centerY"

type AlignedObject = ElementRect & { id: string; rotation?: number }

/** The box around all the objects, rotation included. */
const boundsOf = (objects: AlignedObject[]): ElementRect => {
  const boxes = objects.map((object) => getElementAABB(object, object.rotation || 0))
  const left = Math.min(...boxes.map((box) => box.x))
  const top = Math.min(...boxes.map((box) => box.y))
  const right = Math.max(...boxes.map((box) => box.x + box.width))
  const bottom = Math.max(...boxes.map((box) => box.y + box.height))

  return {
    x: left, y: top, width: right - left, height: bottom - top,
  }
}

/**
 * New positions that line the objects up on an edge or a centre line, by what they look like
 * (rotation included): against each other, or against `container` when given (one object
 * aligned to its frame).
 */
const alignObjects = (objects: AlignedObject[], alignment: Alignment, container?: ElementRect) => {
  const target = container ?? boundsOf(objects)

  return new Map(objects.map((object) => {
    const box = getElementAABB(object, object.rotation || 0)
    const dx = {
      left: target.x - box.x,
      right: target.x + target.width - (box.x + box.width),
      centerX: target.x + target.width / 2 - (box.x + box.width / 2),
      top: 0,
      bottom: 0,
      centerY: 0,
    }[alignment]
    const dy = {
      top: target.y - box.y,
      bottom: target.y + target.height - (box.y + box.height),
      centerY: target.y + target.height / 2 - (box.y + box.height / 2),
      left: 0,
      right: 0,
      centerX: 0,
    }[alignment]

    return [object.id, { x: object.x + dx, y: object.y + dy }]
  }))
}

export default alignObjects
